/**
 * Created by Ning Jiang on 2021-06-22.
 */

import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {OktoberfestLayers, Layers} from "../../../view/OktoberfestLayers";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {Oktoberfest} from "../../../Oktoberfest";
import {gsap, TimelineLite} from "gsap";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {MultiSprites} from "@nolimitcity/slot-game/bin/animation/MultiSprites";
import Graphics = PIXI.Graphics;

export interface IBannerConfig {
    scaleX: number;
    scaleY: number;
    rotation: number;
    y: number;
    x: number;
}

export class BigWinView extends PIXI.Container {

    private static BANNERS_CONFIG: IBannerConfig[][] = [
        [
            {x: -365, y: -11, scaleX: 1.3, scaleY: 1.3, rotation: 15 / 180 * Math.PI},
            {x: 310, y: 764, scaleX: 1, scaleY: 1, rotation: 320 / 180 * Math.PI}
        ],
        [
            {x: -763, y: 636, scaleX: 1, scaleY: 1, rotation: 330 / 180 * Math.PI},
            {x: 419, y: 614, scaleX: 1, scaleY: 1, rotation: 310 / 180 * Math.PI}
        ],
        [
            {x: -362, y: -24, scaleX: 1, scaleY: 1, rotation: 40 / 180 * Math.PI},
            {x: -336, y: 636, scaleX: 1.1, scaleY: 1.1, rotation: 330 / 180 * Math.PI}
        ]
    ];
    private static bigWinTexts: string[] = [
        "Groß Gewinn",
        "Super Lecker",
        "Zauberhaft Spitzenklasse"
    ];
    private _layer: StageLayer;
    private _bannerAnimations: TimelineSprite[];
    private _bannerContainer: PIXI.Container;
    private _isLandscape: boolean;
    private _isPlaying: boolean = false;
    private _slide: MultiSprites;
    private _dimOverLay: Graphics;

    constructor() {
        super();
        this._layer = OktoberfestLayers.getLayer(Layers.BIG_WIN)!;
        this._bannerContainer = OktoberfestLayers.getLayer(Layers.BIG_WIN_BANNER)!;
        this.position.set(360, 390);
        this.createDimBackGround();
        this._layer.addChild(this);
        this.createBanners();
        this._slide = new MultiSprites();
        this._slide.position.set(0, -100);
        for (let level: number = 0; level < 3; level++) {
            this._slide.addSprites({
                name: `level${level}`,
                textures: Oktoberfest.assetLoader.getTextures(`bw_step${level + 1}`),
                anchor: new PIXI.Point(0.5, 0.5)
            });
            this._slide.addSprites({
                name: `levelOut${level}`,
                textures: Oktoberfest.assetLoader.getTextures(`bw_step_outro${level + 1}`),
                anchor: new PIXI.Point(0.5, 0.5)
            });
        }
        this.addChild(this._slide);
    }

    public onOrientationChanged(): void {
        if (this._bannerAnimations.length > 0) {
            if (NolimitApplication.isLandscape && this._bannerAnimations[0].x === -773) {
                this._bannerAnimations[0].x = -877;
            } else if (this._bannerAnimations[0].x === -877) {
                this._bannerAnimations[0].x = -773;
            }
        }
        this._dimOverLay.clear();
        this._dimOverLay.beginFill(0x000000, 0.4);
        this._dimOverLay.drawRect(-280, 0,
            NolimitApplication.screenBounds.width,
            NolimitApplication.screenBounds.height);
        this._dimOverLay.endFill();
        if (!this._isPlaying || this._isLandscape === NolimitApplication.isLandscape) {
            return;
        }
        this._isLandscape = NolimitApplication.isLandscape;
    }

    public getLevelAnimation(level: number): TimelineLite {
        for (let bannerId: number = 0; bannerId < 2; bannerId++) {
            const config: IBannerConfig = BigWinView.BANNERS_CONFIG[level][bannerId];
            const banner: TimelineSprite = this._bannerAnimations[bannerId];
            banner.getAnimationAutoShowHide(true, false);
            banner.x = config.x;
            banner.y = config.y;
            banner.scale.x = config.scaleX;
            banner.scale.y = config.scaleY;
            banner.rotation = config.rotation;
        }
        this._isPlaying = true;
        this.onOrientationChanged();
        if (level == 0) {
            this._dimOverLay.alpha = 0.4;
        }
        const tl: TimelineLite = gsap.timeline();
        if (level > 0) {
            tl.add(
                [this._slide.playSpriteFromTo(`levelOut${level - 1}`,
                    {alpha: 0},
                    {duration: 0.23, alpha: 1},
                    {duration: 0.01, alpha: 0}),
                ]);
        }
        tl.add(
            [this._slide.playSpriteFromTo(`level${level}`,
                {alpha: 0},
                {duration: 1, alpha: 1},
                {duration: 1, alpha: 0}),
            ]);

        return tl;
    }

    public playEnding(): TimelineLite {
        this._isPlaying = false;
        const bigWinLastLevel: number = Oktoberfest.model.parsedGameData.currentWinRatioConfig!.bigWinLevel;
        const tl: TimelineLite = gsap.timeline({
            onComplete: () => {
                Oktoberfest.initialCountUp.setText(BigWinView.bigWinTexts[bigWinLastLevel]);
            }
        });
        this._dimOverLay.alpha = 0;
        tl.add(
            [
                this._slide.playSpriteFromTo(`levelOut${bigWinLastLevel}`,
                    {alpha: 0},
                    {duration: 0.23, alpha: 1},
                    {duration: 0.01, alpha: 0})
            ], 0.01);
        tl.add([
            () => {
                Oktoberfest.bigWinCountUp.remove();
                for (let bannerId: number = 0; bannerId < 2; bannerId++) {
                    this._bannerAnimations[bannerId].hide();
                }
            }
        ], 0.01);
        return tl;
    }

    private createDimBackGround(): void {
        this._dimOverLay = new Graphics();
        this._dimOverLay.beginFill(0x000000, 0.4);
        this._dimOverLay.drawRect(-280, 0,
            NolimitApplication.screenBounds.width,
            NolimitApplication.screenBounds.height);
        this._dimOverLay.endFill();
        this._dimOverLay.alpha = 0;
        this._bannerContainer.addChild(this._dimOverLay);
    }

    private createBanners(): void {
        this._bannerContainer.name = "bannerContainer";
        this._bannerAnimations = [];
        for (let bannerId: number = 0; bannerId < 2; bannerId++) {
            let banner: TimelineSprite = new TimelineSprite(Oktoberfest.assetLoader.getTextures("bw_banner"));
            this._bannerAnimations.push(banner);
            this._bannerContainer.addChild(banner);
            banner.hide();
        }
    }

}